// HPC Report - PartDPage5 (Overview - Read-only, A4 for printing)
import React from 'react';

const PartDPage5 = ({ data }) => {
  const studentFields = data?.student_fields || {};
  const teacherFields = data?.teacher_fields || {};

  // Helper to render Lined Paper Note
  const renderLinedPaper = (text, title) => (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '180px',
      backgroundColor: '#fff',
      backgroundImage: 'linear-gradient(#999 1px, transparent 1px)',
      backgroundSize: '100% 24px',
      border: '1px solid #333',
      borderRadius: '2px',
      padding: '0',
      overflow: 'hidden',
      boxShadow: '2px 2px 5px rgba(0,0,0,0.1)'
    }}>
      {/* Red margin line */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: '30px',
        bottom: 0,
        width: '1px',
        backgroundColor: '#ff0000',
        opacity: 0.5
      }} />

      {/* Holes */}
      <div style={{ position: 'absolute', top: '10px', left: '10px', width: '10px', height: '10px', borderRadius: '50%', border: '1px solid #999', backgroundColor: '#fff' }} />
      <div style={{ position: 'absolute', top: '10px', left: '40px', width: '10px', height: '10px', borderRadius: '50%', border: '1px solid #999', backgroundColor: '#fff' }} />
      <div style={{ position: 'absolute', top: '10px', left: '70px', width: '10px', height: '10px', borderRadius: '50%', border: '1px solid #999', backgroundColor: '#fff' }} />
      <div style={{ position: 'absolute', top: '10px', left: '100px', width: '10px', height: '10px', borderRadius: '50%', border: '1px solid #999', backgroundColor: '#fff' }} />

      {/* Curled Corner Effect (Bottom Right) */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '40px',
        height: '40px',
        background: 'linear-gradient(135deg, transparent 50%, #f0f0f0 50%)',
        boxShadow: '-2px -2px 5px rgba(0,0,0,0.1)',
        borderTop: '1px solid #ccc',
        borderLeft: '1px solid #ccc',
        borderBottomRightRadius: '2px'
      }} />

      {/* Content */}
      <div style={{
        padding: '30px 10px 10px 40px',
        fontSize: '12px',
        lineHeight: '24px',
        fontFamily: 'Comic Sans MS, cursive, sans-serif',
        color: '#333',
        height: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '5px', lineHeight: '1.2' }}>{title}</div>
        {text || ''}
      </div>
    </div>
  );

  // Helper to render Performance Grid
  const renderPerformanceGrid = (levels) => {
    const categories = ['Awareness', 'Sensitivity', 'Creativity'];
    const performanceLevels = [
      { label: 'Beginner\n(0-1)', value: 'Beginner' },
      { label: 'Proficient\n(2-3)', value: 'Proficient' },
      { label: 'Advanced\n(4-5)', value: 'Advanced' }
    ];

    // Adjust levels for Learner/Peer if needed (image shows slightly different ranges/labels but structure is same)
    // Image shows: Beginner (0-1), Proficient (2), Advanced (3) for Learner/Peer
    // But let's stick to a consistent structure or pass props if strict difference needed.
    // For now using the Teacher scale as default, but can override.

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {categories.map((cat) => (
          <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Category Label */}
            <div style={{
              backgroundColor: '#2C3E50', // Dark blue from image
              color: 'white',
              padding: '8px',
              width: '100px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '11px',
              borderRadius: '2px'
            }}>
              {cat}
            </div>

            {/* Levels */}
            {performanceLevels.map((level, idx) => {
              const isChecked = levels?.[`${cat.toLowerCase()}_level`] === level.value;
              return (
                <div key={idx} style={{ position: 'relative', width: '60px', height: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Checkbox */}
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '1px solid #333',
                    backgroundColor: 'white',
                    marginBottom: '-8px',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px'
                  }}>
                    {isChecked ? '✓' : ''}
                  </div>

                  {/* Circle */}
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: '#F9E79F', // Light yellow
                    border: '1px solid #F1C40F',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    fontSize: '9px',
                    padding: '2px',
                    boxSizing: 'border-box',
                    lineHeight: '1.1'
                  }}>
                    {level.label}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="report-page" style={{
      width: '210mm',
      minHeight: '297mm',
      margin: '0 auto',
      padding: '20mm',
      backgroundColor: 'white',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)',
      position: 'relative',
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box'
    }}>
      {/* Top-right corner */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '0 80px 80px 0',
        borderColor: 'transparent #F4824C transparent transparent'
      }} />

      {/* Main Header */}
      <div style={{
        backgroundColor: '#F4824C',
        color: 'white',
        textAlign: 'center',
        padding: '10px',
        marginBottom: '2px'
      }}>
        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Overview</div>
        <div style={{ fontSize: '12px' }}>Tabulate all the feedback for the classroom interaction on this page.</div>
      </div>

      {/* 1. Teacher Assessment */}
      <div style={{ marginBottom: '25px' }}>
        <div style={{
          backgroundColor: '#F4824C',
          color: 'white',
          padding: '5px 15px',
          fontWeight: 'bold',
          fontSize: '14px',
          display: 'inline-block',
          marginBottom: '10px'
        }}>
          Teacher Assessment
        </div>
        <div style={{ display: 'flex', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            {renderLinedPaper(teacherFields.appreciation_comments, "Write some words of appreciation for the learner about their performance.")}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px', textAlign: 'right' }}>
              Tick (✓) the final performance level based on the score.
            </div>
            {renderPerformanceGrid(teacherFields.performance_levels)}
          </div>
        </div>
      </div>

      {/* 2. Learner Reflection */}
      <div style={{ marginBottom: '25px' }}>
        <div style={{
          backgroundColor: '#4A7BA7',
          color: 'white',
          padding: '5px 15px',
          fontWeight: 'bold',
          fontSize: '14px',
          display: 'inline-block',
          marginBottom: '10px'
        }}>
          Learner Reflection
        </div>
        <div style={{ display: 'flex', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            {renderLinedPaper(studentFields.learner_reflection?.self_appreciation, "Write some words of appreciation for yourself.")}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px', textAlign: 'right' }}>
              Tick (✓) the final performance level based on the score.
            </div>
            {renderPerformanceGrid(studentFields.learner_reflection?.performance_levels)}
          </div>
        </div>
      </div>

      {/* 3. Peer Feedback */}
      <div style={{ marginBottom: '25px' }}>
        <div style={{
          backgroundColor: '#F4B400',
          color: 'black',
          padding: '5px 15px',
          fontWeight: 'bold',
          fontSize: '14px',
          display: 'inline-block',
          marginBottom: '10px'
        }}>
          Peer Feedback
        </div>
        <div style={{ display: 'flex', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            {renderLinedPaper(studentFields.peer_feedback?.peer_appreciation, "Write some words of appreciation for your peer about their performance.")}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px', textAlign: 'right' }}>
              Tick (✓) the final performance level based on the score.
            </div>
            {renderPerformanceGrid(studentFields.peer_feedback?.performance_levels)}
          </div>
        </div>
      </div>

      {/* Page Number */}
      <div style={{
        position: 'absolute',
        bottom: '15mm',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '11px',
        color: '#666',
        fontWeight: 'bold'
      }}>
        35
      </div>

      {/* Bottom corners */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '60px 0 0 60px',
        borderColor: 'transparent transparent transparent #F4824C'
      }} />
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '60px 60px 0 0',
        borderColor: 'transparent #F4824C transparent transparent',
        opacity: 0.3
      }} />
    </div>
  );
};

export default PartDPage5;
